import React from "react";
import { Clock, Warning, CheckCircle, XCircle, Hourglass } from "@phosphor-icons/react";

/* ============================================================================
   SLAPill — unified SLA state pill for the Leads Hub
   ----------------------------------------------------------------------------
   States:
       within        — clock running, < 75% of budget used
       at_risk       — clock running, 75-100% of budget
       breached      — clock running, > 100% of budget (no first touch yet)
       met           — first touch happened in time
       missed        — first touch happened late (or never)
       not_assigned  — not yet on the clock
   Used in Leads Hub list rows, detail header, and dashboard tiles.
   ========================================================================== */

const STATES = {
    within: {
        Icon: Clock, label: "On track",
        cls: "bg-emerald-100 text-emerald-800 border-emerald-200",
    },
    at_risk: {
        Icon: Hourglass, label: "At risk",
        cls: "bg-amber-100 text-amber-800 border-amber-200",
    },
    breached: {
        Icon: Warning, label: "Breached",
        cls: "bg-rose-100 text-rose-800 border-rose-200",
    },
    met: {
        Icon: CheckCircle, label: "Met",
        cls: "bg-emerald-100 text-emerald-800 border-emerald-200",
    },
    missed: {
        Icon: XCircle, label: "Missed",
        cls: "bg-rose-100 text-rose-800 border-rose-200",
    },
    not_assigned: {
        Icon: Clock, label: "Not assigned",
        cls: "bg-slate-100 text-slate-600 border-slate-200",
    },
};

function _fmtHours(h) {
    if (h == null) return null;
    if (h < 1) return `${Math.round(h * 60)}m`;
    if (h < 24) return `${h.toFixed(1)}h`;
    return `${Math.round(h / 24)}d`;
}

export function SLAPill({ sla, kind, size = "sm", testid }) {
    if (!sla) return null;
    const s = STATES[sla.state] || STATES.not_assigned;
    const isSm = size === "sm";
    const elapsed = _fmtHours(sla.elapsed_hours);
    const target = sla.hours_target;
    const detail = elapsed && sla.state !== "not_assigned"
        ? ` · ${elapsed}${target ? `/${target}h` : ""}`
        : "";
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full border font-bold tracking-wide ${s.cls} ${
                isSm ? "px-2 py-0.5 text-[10px] uppercase" : "px-2.5 py-1 text-xs"
            }`}
            data-testid={testid || `sla-pill-${sla.state}`}
            title={target ? `${kind || "lead"} SLA: ${target}h` : ""}
        >
            <s.Icon size={isSm ? 10 : 12} weight={sla.state === "within" || sla.state === "met" ? "bold" : "fill"} />
            {s.label}{detail}
        </span>
    );
}

export default SLAPill;
